import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Plus, Search, ChevronLeft, ChevronRight, Package, RefreshCw, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../services/api';
import { toast } from 'sonner';

const XDConnectsImport = ({ onImported }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [lookupRef, setLookupRef] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [importingRef, setImportingRef] = useState(null);

  const fetchProducts = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.get(`/provider/import/xdconnects/browse?page=${p}`);
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setItemsTotal(res.data.items_total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement XD Connects');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(page); }, [page, fetchProducts]);

  const handleImport = async (product) => {
    setImportingRef(product.model_code);
    try {
      await api.post('/provider/import/xdconnects/add-single', { product });
      toast.success(`${product.name} ajoute au catalogue !`);
      onImported();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import'); }
    setImportingRef(null);
  };

  const handleLookup = async () => {
    if (!lookupRef.trim()) return;
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const res = await api.get(`/provider/import/xdconnects/lookup/${lookupRef.trim()}`);
      setLookupResult(res.data.product);
      toast.success('Produit trouve !');
    } catch (e) { toast.error(e.response?.data?.detail || 'Produit non trouve'); }
    setLookupLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/provider/import/xdconnects/refresh');
      toast.success(res.data.message);
      fetchProducts(1);
      setPage(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur actualisation'); }
    setRefreshing(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="xdconnects-import">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Zap className="w-6 h-6 text-purple-600" />
          <h3 className="font-heading font-bold text-base uppercase">Import XD Connects</h3>
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 font-bold rounded">{total} modeles</span>
          <span className="text-[10px] text-slate-400">{itemsTotal} variantes</span>
        </div>
        <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={handleRefresh} disabled={refreshing} data-testid="xdconnects-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser le catalogue
        </Button>
      </div>

      {/* Lookup */}
      <div className="bg-white border p-4 mb-4">
        <h4 className="font-heading font-bold uppercase text-xs mb-2">Recherche par reference</h4>
        <div className="flex gap-2">
          <Input value={lookupRef} onChange={e => setLookupRef(e.target.value)} placeholder="Ex: 100804" className="max-w-xs" data-testid="xdconnects-lookup-input" onKeyDown={e => e.key === 'Enter' && handleLookup()} />
          <Button onClick={handleLookup} disabled={lookupLoading} className="gap-2 bg-purple-600 text-white" data-testid="xdconnects-lookup-btn">
            {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Chercher
          </Button>
        </div>
        {lookupResult && (
          <div className="mt-3 border p-3 flex items-center gap-4 bg-purple-50">
            {lookupResult.image_url ? <img src={lookupResult.image_url} alt="" className="w-16 h-16 object-contain rounded" /> : <Package className="w-16 h-16 text-slate-200" />}
            <div className="flex-1">
              <p className="font-heading font-bold text-sm">{lookupResult.name}</p>
              <p className="text-xs text-slate-500">{lookupResult.model_code} | {lookupResult.price}€ | {lookupResult.colors?.length || 0} couleurs | Stock: {lookupResult.total_stock}</p>
            </div>
            <Button size="sm" className="bg-purple-600 text-white gap-1" onClick={() => { handleImport(lookupResult); setLookupResult(null); setLookupRef(''); }} data-testid="xdconnects-lookup-add">
              <Plus className="w-3 h-3" /> Ajouter
            </Button>
          </div>
        )}
      </div>

      {/* Products grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <p className="text-sm text-slate-500">Chargement du catalogue XLSX...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
            <AnimatePresence>
              {products.map(p => (
                <motion.div key={p.model_code} className="bg-white border overflow-hidden group hover:shadow-md transition-shadow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={`xd-product-${p.model_code}`}>
                  <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden relative">
                    {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-contain p-2" loading="lazy" /> : <Package className="w-16 h-16 text-slate-200" />}
                    {p.total_stock > 0 && <span className="absolute top-2 right-2 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">{p.total_stock} en stock</span>}
                    {p.total_stock === 0 && <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">Rupture</span>}
                  </div>
                  <div className="p-3">
                    <p className="font-heading font-bold text-xs leading-tight line-clamp-2 mb-1">{p.name}</p>
                    <p className="text-[10px] text-slate-400 mb-1">{p.model_code} | {p.brand}</p>
                    <p className="text-[10px] text-slate-500 mb-2">{p.main_category} / {p.sub_category}</p>
                    {p.colors?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {p.colors.slice(0, 6).map(c => (
                          <span key={c} className="bg-slate-100 text-[9px] px-1.5 py-0.5 text-slate-600 rounded">{c}</span>
                        ))}
                        {p.colors.length > 6 && <span className="text-[9px] text-slate-400">+{p.colors.length - 6}</span>}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-heading font-bold text-sm text-purple-700">{p.price}€</span>
                      <span className="text-[10px] text-slate-400">{p.variants?.length || 0} var.</span>
                    </div>
                    {p.pricing?.length > 1 && (
                      <div className="mt-2 bg-purple-50 border border-purple-200 rounded p-1.5">
                        <p className="text-[9px] font-bold text-purple-700 mb-0.5">Degressif</p>
                        {p.pricing.slice(0, 3).map((tier, i) => (
                          <div key={i} className="flex justify-between text-[10px]">
                            <span className="text-slate-600">{tier.qty}+ pcs</span>
                            <span className="font-mono font-bold text-purple-700">{tier.price}€</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button size="sm" className="w-full mt-2 bg-purple-600 text-white text-xs gap-1" disabled={importingRef === p.model_code} onClick={() => handleImport(p)} data-testid={`import-xd-${p.model_code}`}>
                      {importingRef === p.model_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Ajouter au catalogue
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <div className="flex items-center justify-center gap-3 pb-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="xdconnects-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs font-heading">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="xdconnects-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default XDConnectsImport;
